import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import Handler from "../../../Helpers/Handler";
import { textCapitalize } from "../../../Helpers/Utils";
import { Skeleton } from "../../../Components/Needed";

const METHODS = [
    { key: "aadhaar", label: "Aadhaar verification", hint: "Require Aadhaar KYC before a customer can book." },
    { key: "dl", label: "Driving licence verification", hint: "Require a verified DL before a customer can book." },
    { key: "third", label: "Third document", hint: "Require one more identity document (voter ID / passport / PAN)." },
];

const THIRD_DOC_TYPES = ["voter", "passport", "pan"];

const VerificationTab = () => {
    const [loading, setLoading] = useState(true);
    const [methods, setMethods] = useState({ aadhaar: false, dl: false, third: false });
    const [savingMethod, setSavingMethod] = useState(null);
    const [allowedTypes, setAllowedTypes] = useState([]);
    const [expiryGraceDays, setExpiryGraceDays] = useState("0");
    const [savingPolicy, setSavingPolicy] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        const response = await Handler({ method: "GET", url: "/staff/business/site/verification/view" });
        if (response?.success) {
            const data = response?.data?.data || {};
            setMethods({ aadhaar: !!data.aadhaar, dl: !!data.dl, third: !!data.third });
            setAllowedTypes(Array.isArray(data.thirdDocumentAllowedTypes) ? data.thirdDocumentAllowedTypes : []);
            setExpiryGraceDays(String(data.expiryGraceDays ?? 0));
        }
        setLoading(false);
    }, []);

    useEffect(() => { load(); }, [load]);

    const toggleMethod = async (key) => {
        const nextValue = !methods[key];
        setSavingMethod(key);
        const response = await Handler({
            method: "patch",
            url: "/staff/business/site/verification",
            data: { verification: key, verificationStatus: nextValue },
        });
        if (response?.success) {
            setMethods((prev) => ({ ...prev, [key]: nextValue }));
            toast.success(response?.data?.message || "Updated");
        } else {
            toast.error(textCapitalize(response?.data?.message || "Unable to update"));
        }
        setSavingMethod(null);
    };

    const toggleAllowedType = (type) => {
        setAllowedTypes((prev) => (prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]));
    };

    const onSavePolicy = async () => {
        if (methods.third && allowedTypes.length === 0) {
            toast.error("Select at least one accepted document type");
            return;
        }
        const graceDays = Number(expiryGraceDays);
        if (Number.isNaN(graceDays) || graceDays < 0) {
            toast.error("Expiry grace days must be a non-negative number");
            return;
        }
        setSavingPolicy(true);
        const response = await Handler({
            method: "patch",
            url: "/staff/business/site/verification/policy",
            data: { thirdDocumentAllowedTypes: allowedTypes, expiryGraceDays: graceDays },
        });
        if (response?.success) {
            toast.success(response?.data?.message || "Verification policy updated");
            load();
        } else {
            toast.error(textCapitalize(response?.data?.message || "Unable to update verification policy"));
        }
        setSavingPolicy(false);
    };

    if (loading) {
        return (
            <div className="settings-card">
                <Skeleton height={24} width="40%" className="mb-3" />
                <Skeleton height={38} className="mb-3" />
                <Skeleton height={38} className="mb-3" />
            </div>
        );
    }

    return (
        <>
            <div className="settings-card mb-4">
                <h5 className="fw-semibold mb-3">Customer Verification Methods</h5>
                <p className="text-muted small mb-4">Each toggle saves immediately.</p>

                {METHODS.map((method) => (
                    <div className="settings-toggle-row" key={method.key}>
                        <div>
                            <div className="fw-semibold">{method.label}</div>
                            <div className="text-muted small">{method.hint}</div>
                        </div>
                        <div className="form-check form-switch fs-4 m-0">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                role="switch"
                                aria-label={method.label}
                                checked={!!methods[method.key]}
                                disabled={savingMethod === method.key}
                                onChange={() => toggleMethod(method.key)}
                            />
                        </div>
                    </div>
                ))}
            </div>

            <div className="settings-card">
                <h5 className="fw-semibold mb-3">Third Document Policy</h5>
                <p className="text-muted small mb-4">Only applies while "Third document" above is enabled.</p>

                <div className="mb-4">
                    <label className="form-label d-block">Accepted document types</label>
                    <div className="d-flex flex-wrap gap-3">
                        {THIRD_DOC_TYPES.map((type) => (
                            <div className="form-check" key={type}>
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id={`third-doc-${type}`}
                                    checked={allowedTypes.includes(type)}
                                    onChange={() => toggleAllowedType(type)}
                                />
                                <label className="form-check-label text-capitalize" htmlFor={`third-doc-${type}`}>{type}</label>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="col-12 col-md-4 mb-4">
                    <label className="form-label" htmlFor="verification-grace-days">Expiry grace period (days)</label>
                    <input
                        id="verification-grace-days"
                        type="number"
                        min="0"
                        className="form-control"
                        value={expiryGraceDays}
                        onChange={(e) => setExpiryGraceDays(e.target.value)}
                    />
                    <div className="form-text">How many days after a document expires it's still accepted.</div>
                </div>

                <div className="settings-card-actions">
                    <button type="button" className="btn btn-primary" onClick={onSavePolicy} disabled={savingPolicy}>
                        {savingPolicy ? "Saving..." : "Save policy"}
                    </button>
                </div>
            </div>
        </>
    );
};

export default VerificationTab;
